export type AppDraftGuard = {
  ownerKey: string;
  isUnsafeToLeave: () => boolean;
  settleBeforeLeave: () => Promise<boolean>;
};

type NavigationAttempt = {
  action: () => void;
  reject?: () => void;
  channel?: object;
  resolve: (accepted: boolean) => void;
};

/** One coordinator for both router transitions and intents that change UI state first. */
export function createDraftNavigationGuard() {
  const guards = new Map<symbol, AppDraftGuard>();
  const settlements = new Map<symbol, Promise<boolean>>();
  const listeners = new Set<() => void>();
  let pending: NavigationAttempt | undefined;

  function isUnsafe() {
    for (const guard of guards.values()) {
      // A broken guard must never authorize losing its owner's draft.
      try {
        if (guard.isUnsafeToLeave()) return true;
      } catch {
        return true;
      }
    }
    return false;
  }

  function refresh() {
    for (const listener of listeners) listener();
  }

  function cancel(attempt: NavigationAttempt, notify = true) {
    if (pending === attempt) pending = undefined;
    if (notify) attempt.reject?.();
    attempt.resolve(false);
  }

  function settle(key: symbol, guard: AppDraftGuard) {
    const existing = settlements.get(key);
    if (existing) return existing;
    const promise = Promise.resolve()
      .then(() => (guards.get(key) === guard ? guard.settleBeforeLeave() : false))
      .catch(() => false)
      .finally(() => {
        if (settlements.get(key) === promise) settlements.delete(key);
        refresh();
      });
    settlements.set(key, promise);
    return promise;
  }

  function request(action: () => void, options: {reject?: () => void; channel?: object} = {}): Promise<boolean> {
    if (pending) {
      // React Router replaces its single blocker on repeated Back/link clicks.
      // Resetting the previous blocker would also erase that newer transition.
      const replacesSameChannel = options.channel !== undefined && pending.channel === options.channel;
      cancel(pending, !replacesSameChannel);
    }
    if (!isUnsafe()) {
      action(); // Preserve the site's synchronous navigation when no draft needs saving.
      return Promise.resolve(true);
    }

    return new Promise<boolean>((resolve) => {
      const attempt: NavigationAttempt = {action, resolve, ...options};
      pending = attempt;
      const work = [...guards].map(([key, guard]) => settle(key, guard));
      void Promise.all(work).then((saved) => {
        if (pending !== attempt) return;
        // Save completion alone is insufficient: another keystroke may have
        // created a newer revision while the request was in flight.
        if (!saved.every(Boolean) || isUnsafe()) {
          cancel(attempt);
          return;
        }
        pending = undefined;
        action();
        resolve(true);
      });
    });
  }

  return {
    isUnsafe,
    refresh,
    request,
    register(guard: AppDraftGuard) {
      const key = Symbol(guard.ownerKey);
      guards.set(key, guard);
      refresh();
      return () => {
        guards.delete(key);
        // An auth/session teardown or a different record must not inherit an
        // old asynchronous navigation intent when the former save finishes.
        if (pending) cancel(pending);
        refresh();
      };
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export type DraftNavigationGuard = ReturnType<typeof createDraftNavigationGuard>;

export function preventUnsafeUnload(
  guard: DraftNavigationGuard,
  event: Pick<BeforeUnloadEvent, 'preventDefault' | 'returnValue'>,
) {
  if (!guard.isUnsafe()) return;
  event.preventDefault();
  event.returnValue = '';
}
